import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../assets/appLogoFinalLight.png';

export const generateDensityReportPDF = async (projectData, measurements, reportOptions = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const primaryColor = '#1f2937'; // Dark gray
  const secondaryColor = '#374151'; // Medium gray
  const accentColor = '#059669'; // Green for pass
  const failColor = '#dc2626'; // Red for fail

  // Simplified quality thresholds - only compaction
  const thresholds = {
    compaction: {
      min: reportOptions.compactionMin || 92.0, // This IS the pass threshold
      max: reportOptions.compactionMax || null,
      enableMax: reportOptions.compactionEnableMax || false,
    },
  };




 /**
 * Generates a static Google Maps image with measurement markers
 * Handles large datasets by clustering and limiting markers
 */
async function generateSiteMap(measurements, thresholds, mapOptions = {}) {
    const { mapType = 'satellite', size = '800x480' } = mapOptions;
  
    // Filter measurements with valid coordinates
    const validMeasurements = measurements.filter(m => 
      m.latitude != null && m.longitude != null && 
      !isNaN(m.latitude) && !isNaN(m.longitude)
    );
  
    if (validMeasurements.length === 0) {
      console.warn('No measurements with valid coordinates found');
      return null;
    }
  
    try {
      return await generateGoogleStaticMap(validMeasurements, thresholds, { mapType, size });
    } catch (error) {
      console.error('Error generating static map:', error);
      throw error;
    }
  }
  
  /**
   * Google Maps Static API implementation with marker optimization
   */
  async function generateGoogleStaticMap(measurements, thresholds, options = {}) {
    const { size = '800x480', mapType = 'satellite' } = options;
    
    // Your actual Google Maps API key
    const apiKey = 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw';
    
    if (!apiKey) {
      throw new Error('Google Maps API key is required');
    }
  
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const params = new URLSearchParams({
      size,
      maptype: mapType,
      key: apiKey,
      format: 'png',
      scale: '2'
    });
  
    // Handle large datasets by clustering nearby points and limiting markers
    const optimizedMarkers = optimizeMarkersForUrl(measurements, thresholds);
    
    console.log(`Optimized from ${measurements.length} to ${optimizedMarkers.length} markers`);
  
    // Add optimized markers as simple colored dots
    optimizedMarkers.forEach((marker) => {
      // Simple colored dots without labels
      params.append('markers', 
        `color:${marker.color}|size:tiny|${marker.lat},${marker.lng}`
      );
    });
  
    // Calculate and set center/zoom to fit all points
    const bounds = calculateBounds(measurements);
    const center = `${bounds.centerLat},${bounds.centerLng}`;
    params.set('center', center);
    params.set('zoom', bounds.zoom.toString());
  
    const mapUrl = `${baseUrl}?${params.toString()}`;
    
    // Check URL length (Google has ~8KB limit)
    if (mapUrl.length > 8192) {
      console.warn(`URL length (${mapUrl.length}) may exceed Google's limit. Using simplified markers.`);
      return await generateSimplifiedMap(measurements, thresholds, options);
    }
  
    try {
      const response = await fetch(mapUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      
      // Convert ArrayBuffer to base64 in chunks to avoid stack overflow
      const uint8Array = new Uint8Array(arrayBuffer);
      let binaryString = '';
      const chunkSize = 8192;
      
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        binaryString += String.fromCharCode(...chunk);
      }
      
      return btoa(binaryString);
    } catch (error) {
      console.error('Error fetching Google static map:', error);
      throw error;
    }
  }
  
  /**
   * Fallback for very large datasets - creates a simplified map
   */
  async function generateSimplifiedMap(measurements, thresholds, options) {
    const { size = '800x480', mapType = 'satellite' } = options;
    const apiKey = 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw';
  
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const params = new URLSearchParams({
      size,
      maptype: mapType,
      key: apiKey,
      format: 'png',
      scale: '2'
    });
  
    // Just show the area without individual markers for very large datasets
    const bounds = calculateBounds(measurements);
    const center = `${bounds.centerLat},${bounds.centerLng}`;
    params.set('center', center);
    params.set('zoom', bounds.zoom.toString());
  
    // Add just a summary marker as a colored dot at center
    const passCount = measurements.filter(m => getQualityStatus(m.Compaction, thresholds.compaction) === 'pass').length;
    const failCount = measurements.length - passCount;
    
    // Add one dot showing majority status at center
    const majorityColor = passCount > failCount ? 'green' : 'red';
    
    params.append('markers', `color:${majorityColor}|size:mid|${center}`);
  
    const mapUrl = `${baseUrl}?${params.toString()}`;
    
    try {
      const response = await fetch(mapUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      
      const uint8Array = new Uint8Array(arrayBuffer);
      let binaryString = '';
      const chunkSize = 8192;
      
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        binaryString += String.fromCharCode(...chunk);
      }
      
      return btoa(binaryString);
    } catch (error) {
      console.error('Error fetching simplified map:', error);
      throw error;
    }
  }
  
  /**
 * Optimize markers by clustering nearby points and limiting total count
 * Improved to better represent pass/fail distribution
 */
function optimizeMarkersForUrl(measurements, thresholds, maxMarkers = 200) {
    // First, cluster points that are very close together (within ~10 meters)
    const clustered = clusterNearbyPoints(measurements, 0.00003); // ~5 meter tolerance
    
    // Calculate pass/fail for each cluster
    const markersWithStatus = clustered.map(cluster => {
      const passCount = cluster.points.filter(p => 
        getQualityStatus(p.Compaction, thresholds.compaction) === 'pass'
      ).length;
      const failCount = cluster.points.length - passCount;
      
      return {
        lat: cluster.lat,
        lng: cluster.lng,
        count: cluster.points.length,
        passCount,
        failCount,
        // Show green if ANY passes exist, red only if ALL fail
        color: passCount > 0 ? '0x16A34A' : 'red',
        passRatio: passCount / cluster.points.length
      };
    });
  
    // If too many markers, use smart selection instead of just largest
    if (markersWithStatus.length > maxMarkers) {
      const passes = markersWithStatus.filter(m => m.color === 'green');
      const fails = markersWithStatus.filter(m => m.color === 'red');
      
      // Keep all passes (they're important to show)
      let selected = [...passes];
      
      // Fill remaining slots with largest fail clusters
      const remainingSlots = maxMarkers - passes.length;
      if (remainingSlots > 0) {
        fails.sort((a, b) => b.count - a.count);
        selected = selected.concat(fails.slice(0, remainingSlots));
      }
      
      console.log(`Kept ${passes.length} pass clusters and ${Math.min(remainingSlots, fails.length)} fail clusters`);
      return selected;
    }
  
    return markersWithStatus;
  }
  
  /**
   * Cluster nearby measurement points
   */
  function clusterNearbyPoints(measurements, tolerance = 0.0001) {
    const clusters = [];
    const used = new Set();
  
    measurements.forEach((measurement, index) => {
      if (used.has(index)) return;
  
      const cluster = {
        lat: measurement.latitude,
        lng: measurement.longitude,
        points: [measurement]
      };
  
      // Find nearby points
      measurements.forEach((other, otherIndex) => {
        if (used.has(otherIndex) || index === otherIndex) return;
  
        const distance = Math.sqrt(
          Math.pow(measurement.latitude - other.latitude, 2) +
          Math.pow(measurement.longitude - other.longitude, 2)
        );
  
        if (distance <= tolerance) {
          cluster.points.push(other);
          used.add(otherIndex);
        }
      });
  
      used.add(index);
      clusters.push(cluster);
    });
  
    return clusters;
  }
  
  /**
   * Calculate optimal bounds and zoom for the map
   */
  function calculateBounds(measurements) {
    const lats = measurements.map(m => m.latitude);
    const lngs = measurements.map(m => m.longitude);
    
    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs)
    };
  
    const centerLat = (bounds.north + bounds.south) / 2;
    const centerLng = (bounds.east + bounds.west) / 2;
    
    // Calculate zoom level based on the span
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const maxSpan = Math.max(latSpan, lngSpan);
    
    // Estimate zoom level (this is approximate)
    let zoom = 16; // Default zoom
    if (maxSpan > 0.1) zoom = 11;
    else if (maxSpan > 0.05) zoom = 13;
    else if (maxSpan > 0.01) zoom = 15;
    else if (maxSpan > 0.005) zoom = 16;
    else if (maxSpan > 0.001) zoom = 18;
    else zoom = 19;
  
    return {
      ...bounds,
      centerLat,
      centerLng,
      zoom
    };
  }
  
  /**
   * Check if measurements have location data
   */
  function hasLocationData(measurements) {
    return measurements.some(m => 
      m.latitude != null && m.longitude != null && 
      !isNaN(m.latitude) && !isNaN(m.longitude)
    );
  }

  function addMapLegendClean(doc, x, y, thresholds, measurements) {
    const legendData = [
      ['', 'Pass', `>= ${thresholds.compaction.min}%`],
      ['', 'Fail', `< ${thresholds.compaction.min}%`]
    ];
  
    autoTable(doc, {
      startY: y,
      head: [['Legend', 'Status', 'Criteria']],
      body: legendData,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 2,
        textColor: [0, 0, 0],
        halign: 'center',
      },
      headStyles: {
        fillColor: [31, 41, 55],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
        cellPadding: 1,
      },
      columnStyles: {
        0: { 
          halign: 'center', 
          cellWidth: 17,
        },
        1: { 
          halign: 'center', 
          cellWidth: 20,
          fontStyle: 'bold'
        },
        2: { 
          halign: 'center', 
          cellWidth: 23
        },
      },
      margin: { left: x, right: 15 },
      tableWidth: 60,
      didParseCell: function (data) {
        // Clear the text in the first column and prepare for manual dots
        if (data.column.index === 0 && data.section === 'body') {
          data.cell.text = []; // Clear any text
        }
        
        // Color the status text
        if (data.column.index === 1 && data.section === 'body') {
          if (data.row.index === 0) {
            data.cell.styles.textColor = [34, 197, 94]; // Green
          } else if (data.row.index === 1) {
            data.cell.styles.textColor = [239, 68, 68]; // Red
          }
        }
      },
      didDrawCell: function (data) {
        // Draw manual dots in the first column
        if (data.column.index === 0 && data.section === 'body') {
          const centerX = data.cell.x + data.cell.width / 2;
          const centerY = data.cell.y + data.cell.height / 2;
          
          if (data.row.index === 0) {
            // Green dot for Pass
            doc.setFillColor(34, 197, 94);
            doc.circle(centerX, centerY, 2, 'F');
          } else if (data.row.index === 1) {
            // Red dot for Fail
            doc.setFillColor(239, 68, 68);
            doc.circle(centerX, centerY, 2, 'F');
          }
        }
      }
    });
  }

  // Calculate statistics
  const stats = calculateMeasurementStats(measurements, thresholds);

  // HEADER CONFIGURATION
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, 40, 'F');

  // Report Title
  doc.setFontSize(20);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('COMPACTION REPORT', 15, 24);

  const headerHeight = 40;
  const imgWidth = 60;
  const imgAspect = 2048 / 268;
  const imgHeight = imgWidth / imgAspect;
  const imgX = pageWidth - imgWidth - 15;
  const imgY = (headerHeight - imgHeight) / 2;

  doc.addImage(logo, 'PNG', imgX, imgY, imgWidth, imgHeight);

  // Report metadata
  const reportDate = new Date().toLocaleDateString();
  const reportId = `RPT-${Date.now().toString().slice(-6)}`;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(secondaryColor);
  doc.text(`Report ID: ${reportId}`, 15, 38);
  doc.text(`Generated: ${reportDate}`, pageWidth - 15, 38, { align: 'right' });

  doc.setDrawColor(230);
  doc.setLineWidth(0.5);
  doc.line(15, 42, pageWidth - 15, 42);

  // Company Information
  let currentY = 50;
  if (reportOptions.companyInfo) {
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text(reportOptions.companyInfo.name || 'Company Name', 15, currentY);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(secondaryColor);
    doc.setFontSize(10);
    if (reportOptions.companyInfo.address) {
      currentY += 6;
      doc.text(reportOptions.companyInfo.address, 15, currentY);
    }
    if (reportOptions.companyInfo.phone) {
      currentY += 6;
      doc.text(reportOptions.companyInfo.phone, 15, currentY);
    }
    if (reportOptions.companyInfo.email) {
      currentY += 6;
      doc.text(reportOptions.companyInfo.email, 15, currentY);
    }
    currentY += 10;
  }

  // Project Information
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECT INFORMATION', 15, currentY);

  currentY += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(secondaryColor);

  const projectInfo = [
    ['Project Name:', projectData.project_display_name || projectData.project_name || 'N/A'],
    ['Description:', projectData.project_description || 'N/A'],
    ['Date Range:', getDateRange(measurements)],
    ['Total Measurements:', measurements.length.toString()],
  ];

  projectInfo.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 15, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 80, currentY);
    currentY += 6;
  });


  if (reportOptions.includeMap && hasLocationData(measurements)) {
    currentY += 10;
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text('SITE MAP', 15, currentY);


    // ADD SUBTITLE HERE
    currentY += 6;
    doc.setFontSize(10);
    doc.setTextColor(secondaryColor);
    doc.setFont('helvetica', 'italic');
    doc.text('Note: Not all measurements are shown on the map due to clustering optimization.', 15, currentY);
  
    try {
      const mapImage = await generateSiteMap(measurements, thresholds, reportOptions.mapOptions);
      
      if (mapImage) {
        currentY += 6;
        const mapWidth = pageWidth - 30; // Full width minus margins
        const mapHeight = 120; // Fixed height for consistent layout
        
        // Check if we need a new page
        if (currentY + mapHeight > pageHeight - 50) {
          doc.addPage();
          currentY = 20;
          doc.setFontSize(12);
          doc.setTextColor(primaryColor);
          doc.setFont('helvetica', 'bold');
          doc.text('SITE MAP', 15, currentY);
          currentY += 10;
        }
  
        doc.addImage(mapImage, 'PNG', 15, currentY, mapWidth, mapHeight);
        currentY += mapHeight + 8;
  
        // Add map legend with measurement count info
        addMapLegendClean(doc, 15, currentY, thresholds, measurements);
        currentY += measurements.length > 50 ? 25 : 15; // Extra space if showing cluster note
      }
    } catch (error) {
      console.error('Failed to generate map:', error);
      // Continue without map
      doc.setFontSize(10);
      doc.setTextColor(secondaryColor);
      doc.setFont('helvetica', 'normal');
      doc.text('Map could not be generated', 15, currentY + 8);
      currentY += 15;
    }
  }

  // FORCE NEW PAGE BEFORE QUALITY CONTROL SUMMARY
  doc.addPage();
  currentY = 20;

  // Quality Control Summary - NOW ON NEW PAGE
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('QUALITY CONTROL SUMMARY', 15, currentY);

  // Summary statistics table
  currentY += 5;
  const summaryData = [
    [
      'Compaction',
      `${stats.compaction.average.toFixed(1)}%`,
      `${stats.compaction.passRate.toFixed(1)}%`,
      `${stats.compaction.pass}`,
      `${stats.compaction.fail}`,
      `${stats.compaction.outOfRange || 0}`,
    ],
  ];

  autoTable(doc, {
    startY: currentY,
    head: [['Attribute', 'Average', 'Pass Rate', 'Pass', 'Fail', 'Out of Range']],
    body: summaryData,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 2,
      textColor: [0, 0, 0],
      halign: 'left',
    },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'left',
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: (pageWidth - 30) * 0.2 },
      1: { halign: 'left', cellWidth: (pageWidth - 30) * 0.16 },
      2: { halign: 'left', cellWidth: (pageWidth - 30) * 0.16 },
      3: { halign: 'left', cellWidth: (pageWidth - 30) * 0.16, fillColor: [220, 252, 231] },
      4: { halign: 'left', cellWidth: (pageWidth - 30) * 0.16, fillColor: [254, 226, 226] },
      5: { halign: 'left', cellWidth: (pageWidth - 30) * 0.16, fillColor: [243, 244, 246] },
    },
    margin: { left: 15, right: 15 },
  });

  // Quality Thresholds Table
  currentY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('QUALITY THRESHOLDS', 15, currentY);

  const thresholdData = [
    [
      'Compaction (%)',
      `>= ${thresholds.compaction.min.toFixed(1)}`,
      thresholds.compaction.enableMax && thresholds.compaction.max ? `≤ ${thresholds.compaction.max.toFixed(1)}` : 'N/A',
    ],
  ];

  currentY += 4;
  autoTable(doc, {
    startY: currentY,
    head: [['Parameter', 'Minimum (Pass)', 'Maximum']],
    body: thresholdData,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 2,
      textColor: [0, 0, 0],
      halign: 'left',
    },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'left',
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: (pageWidth - 30) * 0.4 },
      1: { halign: 'left', cellWidth: (pageWidth - 30) * 0.3, fillColor: [220, 252, 231] },
      2: { halign: 'left', cellWidth: (pageWidth - 30) * 0.3, fillColor: [243, 244, 246] },
    },
    margin: { left: 15, right: 15 },
  });

  // Operator Summary
  currentY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('OPERATOR SUMMARY', 15, currentY);

  const operatorStats = calculateOperatorStats(measurements, thresholds);
  const operatorData = Object.entries(operatorStats).map(([operator, stats]) => [
    operator,
    stats.count.toString(),
    `${stats.compactionAvg.toFixed(1)}%`,
    `${stats.passRate.toFixed(1)}%`,
  ]);

  currentY += 4;
  autoTable(doc, {
    startY: currentY,
    head: [['Operator', 'Count', 'Avg Compaction', 'Pass Rate']],
    body: operatorData,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 2,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: (pageWidth - 30) * 0.35 },
      1: { halign: 'left', cellWidth: (pageWidth - 30) * 0.15 },
      2: { halign: 'left', cellWidth: (pageWidth - 30) * 0.25 },
      3: { halign: 'left', cellWidth: (pageWidth - 30) * 0.25 },
    },
    margin: { left: 15, right: 15 },
  });

  // Detailed Measurements Table
  currentY = doc.lastAutoTable.finalY + 20;
  if (currentY > pageHeight - 100) {
    doc.addPage();
    currentY = 20;
  }

  doc.setFontSize(12);
  doc.setTextColor(primaryColor);
  doc.setFont('helvetica', 'bold');
  doc.text('DETAILED MEASUREMENTS', 15, currentY);

  currentY += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(secondaryColor);
  doc.text('Ordered Newest to Oldest', 15, currentY);

  const measurementData = measurements.map((measurement, index) => {
    const compactionStatus = getQualityStatus(measurement.Compaction, thresholds.compaction);
    const status = compactionStatus === 'pass' ? 2 : 0;

    return [
      (index + 1).toString(),
      measurement.operator_name || 'Unknown',
      measurement.Density?.toFixed(1) || 'N/A',
      measurement.Compaction?.toFixed(1) || 'N/A',
      measurement.SurfaceTemp?.toFixed(1) || 'N/A',
      new Date(measurement.LocalTimeOfMeasurement).toLocaleDateString(),
      new Date(measurement.LocalTimeOfMeasurement).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      getStatusSymbol(status),
    ];
  });

  currentY += 6;
  autoTable(doc, {
    startY: currentY,
    head: [['#', 'Operator', 'Density', 'Compaction', 'Temp', 'Date', 'Time', 'Status']],
    body: measurementData,
    theme: 'grid',
    styles: {
      fontSize: 9,
      cellPadding: 2,
      textColor: [0, 0, 0],
      overflow: 'linebreak',
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 2,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: (pageWidth - 30) * 0.06 },
      1: { halign: 'left', cellWidth: (pageWidth - 30) * 0.20 },
      2: { halign: 'left', cellWidth: (pageWidth - 30) * 0.13 },
      3: { halign: 'left', cellWidth: (pageWidth - 30) * 0.13 },
      4: { halign: 'left', cellWidth: (pageWidth - 30) * 0.10 },
      5: { halign: 'left', cellWidth: (pageWidth - 30) * 0.16 },
      6: { halign: 'left', cellWidth: (pageWidth - 30) * 0.10 },
      7: { halign: 'left', cellWidth: (pageWidth - 30) * 0.12, fontStyle: 'bold' },
    },
    didParseCell: function (data) {
      if (data.column.index === 7 && data.section === 'body') {
        const statusText = data.cell.text[0];
        data.cell.statusText = statusText;
        if (statusText === 'PASS') {
          data.cell.styles.fillColor = [220, 252, 231];
        } else if (statusText === 'FAIL') {
          data.cell.styles.fillColor = [254, 226, 226];
        }
        data.cell.text = [];
      }
    },
    didDrawCell: function (data) {
      if (data.column.index === 7 && data.section === 'body' && data.cell.statusText) {
        const statusText = data.cell.statusText;
        const dotX = data.cell.x + 6;
        const dotY = data.cell.y + data.cell.height / 2;
        const textX = data.cell.x + 10;
        const textY = data.cell.y + data.cell.height / 2 + 1;

        if (statusText === 'PASS') {
          doc.setFillColor(22, 163, 74);
          doc.circle(dotX, dotY, 1.5, 'F');
          doc.setTextColor(22, 163, 74);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text('PASS', textX, textY);
        } else if (statusText === 'FAIL') {
          doc.setFillColor(220, 38, 38);
          doc.circle(dotX, dotY, 1.5, 'F');
          doc.setTextColor(220, 38, 38);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text('FAIL', textX, textY);
        }
      }
    },
    margin: { left: 15, right: 15, bottom: 30 },
    didDrawPage: (data) => {
      const pageNumber = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Page ${pageNumber}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
      doc.text(`${projectData.project_display_name || 'Compaction Report'} - ${reportDate}`, 15, pageHeight - 10);
    },
  });

  return doc.output('blob');
};

// Helper functions
function calculateMeasurementStats(measurements, thresholds) {
  const validCompaction = measurements.filter((m) => m.Compaction != null && !isNaN(m.Compaction));
  const compactionStats = calculateAttributeStats(validCompaction, 'Compaction', thresholds.compaction);

  return { compaction: compactionStats };
}

function calculateAttributeStats(measurements, attribute, threshold) {
  if (measurements.length === 0) {
    return { average: 0, passRate: 0, pass: 0, fail: 0, outOfRange: 0 };
  }

  const values = measurements.map((m) => m[attribute]);
  const average = values.reduce((sum, val) => sum + val, 0) / values.length;

  let pass = 0;
  let fail = 0;
  let outOfRange = 0;

  measurements.forEach((m) => {
    const value = m[attribute];
    const status = getQualityStatus(value, threshold);

    if (status === 'pass') {
      pass++;
    } else {
      fail++;
    }

    const belowMin = value < threshold.min;
    const aboveMax = threshold.enableMax && threshold.max && value > threshold.max;
    if (belowMin || aboveMax) {
      outOfRange++;
    }
  });

  const passRate = (pass / measurements.length) * 100;

  return { average, passRate, pass, fail, outOfRange };
}

function calculateOperatorStats(measurements, thresholds) {
  const operatorGroups = measurements.reduce((groups, measurement) => {
    const operator = measurement.operator_name || 'Unknown';
    if (!groups[operator]) {
      groups[operator] = [];
    }
    groups[operator].push(measurement);
    return groups;
  }, {});

  const operatorStats = {};

  Object.entries(operatorGroups).forEach(([operator, measurements]) => {
    const validCompaction = measurements.filter((m) => m.Compaction != null && !isNaN(m.Compaction));

    const compactionAvg =
      validCompaction.length > 0
        ? validCompaction.reduce((sum, m) => sum + m.Compaction, 0) / validCompaction.length
        : 0;

    const passCount = validCompaction.filter(
      (m) => getQualityStatus(m.Compaction, thresholds.compaction) === 'pass',
    ).length;

    const passRate = validCompaction.length > 0 ? (passCount / validCompaction.length) * 100 : 0;

    operatorStats[operator] = {
      count: measurements.length,
      compactionAvg,
      passRate,
    };
  });

  return operatorStats;
}

function getQualityStatus(value, threshold) {
  if (value == null || isNaN(value)) return 'unknown';

  const aboveMin = value >= threshold.min;
  const belowMax = !threshold.enableMax || !threshold.max || value <= threshold.max;

  return aboveMin && belowMax ? 'pass' : 'fail';
}

function getStatusSymbol(status) {
  switch (status) {
    case 2:
      return 'PASS';
    case 0:
      return 'FAIL';
    default:
      return 'N/A';
  }
}

function getDateRange(measurements) {
  if (measurements.length === 0) return 'N/A';

  const dates = measurements
    .map((m) => new Date(m.LocalTimeOfMeasurement))
    .filter((date) => !isNaN(date));

  if (dates.length === 0) return 'N/A';

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  if (minDate.toDateString() === maxDate.toDateString()) {
    return minDate.toLocaleDateString();
  }

  return `${minDate.toLocaleDateString()} - ${maxDate.toLocaleDateString()}`;
}

export const downloadDensityReport = async (projectData, measurements, filename, options = {}) => {
  try {
    const pdfBlob = await generateDensityReportPDF(projectData, measurements, options);
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `density-report-${Date.now()}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating density report:', error);
    throw error;
  }
};